// Predicted delivery dates (13 §13.3): a median and a spread from the history of delivered
// shipments on the same route and service, in the same state. Until a state has 20 samples the SLA
// target stands in (an honest "expected by"). A known future date, the container's ETA, wins over
// history for what happens before arrival. Pure.
import { addDays } from './business-date';
import { percentileSorted, round2 } from './operations-snapshot';
import { medianSorted } from './stats';
import type { LegPointsOut } from './legs';

/** Stages a prediction can start from, in journey order. They are milestone keys. */
export const PREDICTION_STAGES = [
  'collected',
  'loaded',
  'departed',
  'arrived',
  'customs_released',
  'at_hub',
  'with_partner',
] as const;
export type PredictionStage = (typeof PREDICTION_STAGES)[number];

/** Samples a state needs before history replaces the target (13 §13.3). */
export const MIN_PREDICTION_SAMPLES = 20;
/** Samples kept per state: the most recent deliveries. */
export const MAX_STAGE_SAMPLES = 300;
/** The window is the 20th to the 80th percentile of the remaining days. */
export const WINDOW_LOW_PERCENT = 20;
export const WINDOW_HIGH_PERCENT = 80;

const DAY_MS = 86_400_000;

type StageTimes = Record<PredictionStage, string | null>;

/** When the shipment reached each stage, from its leg points. */
export function stageTimes(points: LegPointsOut): StageTimes {
  return {
    collected: points.collectedAt,
    loaded: points.loadedAt,
    departed: points.departedAt,
    arrived: points.arrivedAt,
    customs_released: points.customsReleasedAt,
    at_hub: points.atHubAt,
    with_partner: points.handedOverAt,
  };
}

/** The latest stage a shipment has reached, and when. Null when it has reached none. */
export function currentStage(points: LegPointsOut): { stage: PredictionStage; at: string } | null {
  const times = stageTimes(points);
  for (const stage of [...PREDICTION_STAGES].reverse()) {
    const at = times[stage];
    if (at !== null) return { stage, at };
  }
  return null;
}

export type StageSamples = Record<PredictionStage, number[]>;

/**
 * Remaining days to delivery, per stage, of delivered shipments (newest first, as many as are
 * given; the newest 300 per stage are kept). Ascending within a stage. A shipment that skipped a
 * stage (it has no moment for it) adds nothing to that stage.
 */
export function remainingSamples(delivered: readonly { points: LegPointsOut }[]): StageSamples {
  const out = Object.fromEntries(PREDICTION_STAGES.map((s) => [s, [] as number[]])) as StageSamples;
  for (const row of delivered) {
    const end = row.points.deliveredAt ? Date.parse(row.points.deliveredAt) : Number.NaN;
    if (!Number.isFinite(end)) continue;
    const times = stageTimes(row.points);
    for (const stage of PREDICTION_STAGES) {
      const at = times[stage];
      if (at === null || out[stage].length >= MAX_STAGE_SAMPLES) continue;
      out[stage].push(round2(Math.max(0, end - Date.parse(at)) / DAY_MS));
    }
  }
  for (const stage of PREDICTION_STAGES) out[stage].sort((a, b) => a - b);
  return out;
}

export interface PredictedWindowOut {
  from: string;
  to: string;
  median: string;
  basis: 'history' | 'target';
  samples: number;
  stage: PredictionStage;
}

/** The calendar date (UTC) that is `days` after an instant. */
export function dateAfter(instant: string | number, days: number): string {
  const ms = (typeof instant === 'number' ? instant : Date.parse(instant)) + days * DAY_MS;
  return new Date(ms).toISOString().slice(0, 10);
}

function windowFrom(
  base: string | number,
  values: readonly number[],
  stage: PredictionStage,
  basis: 'history',
): PredictedWindowOut {
  return {
    from: dateAfter(base, percentileSorted(values, WINDOW_LOW_PERCENT)),
    to: dateAfter(base, percentileSorted(values, WINDOW_HIGH_PERCENT)),
    median: dateAfter(base, medianSorted(values)),
    basis,
    samples: values.length,
    stage,
  };
}

export interface PredictInput {
  now: Date;
  stage: PredictionStage;
  /** When the shipment reached `stage`. */
  stageAt: string;
  /** History for this stage: remaining days to delivery, ascending. */
  samples: readonly number[];
  /** The container's ETA, when the shipment is still before arrival. A known future date wins. */
  eta?: string | undefined;
  /** History for the `arrived` stage, used from the ETA. */
  arrivedSamples?: readonly number[] | undefined;
  /** The SLA-based window to fall back on (public-site `predictedWindow`). */
  fallback?: { from: string; to: string } | null | undefined;
}

const BEFORE_ARRIVAL: readonly PredictionStage[] = ['collected', 'loaded', 'departed'];

/**
 * The predicted delivery window of a shipment that has not been delivered.
 *  1. Before arrival with a future ETA and 20 arrived-stage samples: the ETA plus their spread.
 *  2. 20 samples for the stage: the stage's moment plus the median and the 20th/80th percentile of
 *     the remaining days. Shipments already past some samples are measured against those still
 *     open; with fewer than 10 left it is late: today plus the usual spread.
 *  3. Otherwise the SLA window (`basis: 'target'`), or nothing.
 */
export function predictWindow(input: PredictInput): PredictedWindowOut | null {
  const { now, stage } = input;
  const arrived = input.arrivedSamples ?? [];
  if (
    BEFORE_ARRIVAL.includes(stage) &&
    input.eta !== undefined &&
    Date.parse(input.eta) >= now.getTime() &&
    arrived.length >= MIN_PREDICTION_SAMPLES
  ) {
    return windowFrom(input.eta, arrived, stage, 'history');
  }
  if (input.samples.length >= MIN_PREDICTION_SAMPLES) {
    const elapsed = Math.max(0, (now.getTime() - Date.parse(input.stageAt)) / DAY_MS);
    const open = input.samples.filter((r) => r >= elapsed);
    if (open.length >= MIN_PREDICTION_SAMPLES / 2) {
      return windowFrom(input.stageAt, open, stage, 'history');
    }
    const low = percentileSorted(input.samples, WINDOW_LOW_PERCENT);
    const high = percentileSorted(input.samples, WINDOW_HIGH_PERCENT);
    const spread = Math.max(1, high - low);
    const today = now.getTime();
    return {
      from: dateAfter(today, 0),
      to: dateAfter(today, spread),
      median: dateAfter(today, spread / 2),
      basis: 'history',
      samples: input.samples.length,
      stage,
    };
  }
  if (input.fallback) {
    const days = Math.round(
      (Date.parse(`${input.fallback.to}T00:00:00Z`) -
        Date.parse(`${input.fallback.from}T00:00:00Z`)) /
        DAY_MS,
    );
    return {
      from: input.fallback.from,
      to: input.fallback.to,
      median: addDays(input.fallback.from, Math.floor(days / 2)),
      basis: 'target',
      samples: 0,
      stage,
    };
  }
  return null;
}
